import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FileText, ArrowsClockwise, Download, Clock, FileArrowUp, SignOut, GitDiff, FileDoc, Plus, Crown, Ticket, ShieldCheck, ArrowCounterClockwise, FilePlus, User, Info, Question, CaretDown, MusicNote, Sparkle, CreditCard, FilmSlate } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PricingModal } from './dashboard/PricingModal';
import { CompareModal } from './dashboard/CompareModal';
import { SubscriptionsModal } from './dashboard/SubscriptionsModal';
import { ExtensionsModal } from './dashboard/ExtensionsModal';
import { QualityPanel } from './dashboard/QualityPanel';
import { RewriteModal } from './dashboard/RewriteModal';
import { PosterModal } from './dashboard/PosterModal';
import { useDashboardCatalog } from './dashboard/useDashboardData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// File size constants
const KB = 1024;
const MB = KB * KB;
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * MB;

// HTTP status codes
const HTTP_PAYMENT_REQUIRED = 402;

export const Dashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [originalText, setOriginalText] = useState('');
  const [correctedText, setCorrectedText] = useState('');
  const [analysisText, setAnalysisText] = useState('');
  const [qualityRating, setQualityRating] = useState(null);
  const [documentType, setDocumentType] = useState('manuscript');
  const [modelProvider, setModelProvider] = useState('openai');
  const [rushLevel, setRushLevel] = useState('standard');
  const [stylePreset, setStylePreset] = useState('');
  // Flashes the Style menu in rose if user clicks Proofread without picking one
  const [styleError, setStyleError] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [isProofreading, setIsProofreading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [differences, setDifferences] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // Pricing & Payment
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Compare versions
  const [showCompare, setShowCompare] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [compareData, setCompareData] = useState(null);
  
  // Extensions & Subscriptions (data) — fetched via useDashboardCatalog
  const [showExtensions, setShowExtensions] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);
  const { extensions, subscriptionPlans, stylePresets } = useDashboardCatalog();

  // Long-job progress
  const [proofreadProgress, setProofreadProgress] = useState(null); // {current, total}
  
  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const subscribeToPlan = async (planId, promoCode = null) => {
    // Map internal plan_id → Stripe recurring lookup_key (true auto-renewing subs)
    const LOOKUP_KEYS = {
      starter: 'sub_starter_monthly',
      pro: 'sub_pro_monthly',
      unlimited: 'sub_unlimited_monthly',
    };
    const lookup_key = LOOKUP_KEYS[planId];
    if (!lookup_key) {
      toast.error('Unknown plan');
      return;
    }
    setIsProcessingPayment(true);
    try {
      const payload = { lookup_key, origin_url: window.location.origin };
      if (promoCode) payload.promo_code = promoCode;
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        payload,
        { withCredentials: true }
      );
      // Admin bypass — never charged
      if (response.data?.admin_complimentary) {
        toast.success('Complimentary for the host — no payment needed.');
        setIsProcessingPayment(false);
        return;
      }
      if (response.data?.url) window.location.href = response.data.url;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
      setIsProcessingPayment(false);
    }
  };

  const openBillingPortal = async () => {
    try {
      const response = await axios.post(
        `${API}/subscriptions/portal`,
        { return_url: window.location.origin + '/' },
        { withCredentials: true }
      );
      if (response.data?.url) window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open billing portal');
    }
  };

  // Reset style preset to UNSELECTED when document type changes — we want
  // every proofread to be a deliberate style choice by the writer.
  const lastDocTypeRef = useRef(documentType);
  useEffect(() => {
    if (lastDocTypeRef.current === documentType) return;
    lastDocTypeRef.current = documentType;
    Promise.resolve().then(() => {
      setStylePreset('');
      setStyleError(false);
    });
  }, [documentType, stylePresets]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch history:', error);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['txt', 'docx', 'pdf', 'fdx', 'fountain'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error('Unsupported file format. Please upload TXT, DOCX, PDF, FDX, or Fountain files.');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Your file is ${(file.size / MB).toFixed(1)}MB`);
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setOriginalText(response.data.extracted_text);
      setCorrectedText('');
      setDifferences([]);
      setPriceEstimate(null);
      toast.success(`File "${file.name}" uploaded successfully`);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('File upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, []);

  const calculatePrice = async (codeToApply = null, { autoProofreadIfFree = false } = {}) => {
    if (!originalText.trim()) {
      toast.error('Please enter or upload text to proofread');
      return null;
    }

    try {
      const response = await axios.post(
        `${API}/calculate-price`,
        { text: originalText, rush_level: rushLevel, promo_code: codeToApply || null, document_type: documentType },
        { withCredentials: true }
      );
      setPriceEstimate(response.data);

      // If no payment needed (admin / sufficient free words / 100% promo), skip the modal
      // and proofread directly. The Pricing Estimate is only useful when the user has to pay.
      if (autoProofreadIfFree && !response.data.requires_payment) {
        setShowPricing(false);
        // small defer so React state propagates before next call
        setTimeout(() => handleProofread(true), 0);
        return response.data;
      }

      setShowPricing(true);
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Price calculation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to calculate price');
      return null;
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Enter a promo code');
      return;
    }
    setIsApplyingPromo(true);
    try {
      const estimate = await calculatePrice(promoCode.trim().toUpperCase());
      if (estimate && !estimate.requires_payment) {
        toast.success('Promo applied — your job is on us. Starting now...');
        setShowPricing(false);
        setTimeout(() => handleProofread(true), 0);
      } else {
        toast.success('Promo code applied');
      }
    } catch (error) {
      toast.error('Failed to apply code');
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    calculatePrice(null);
  };

  const initiatePayment = async (purchaseType = 'proofread', extensionType = null) => {
    setIsProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const payload = {
        origin_url: originUrl,
        purchase_type: purchaseType,
      };
      
      if (purchaseType === 'extension') {
        payload.extension_type = extensionType;
      } else {
        payload.word_count = priceEstimate.word_count;
        payload.rush_level = rushLevel;
        if (priceEstimate.promo_code_applied) {
          payload.promo_code = priceEstimate.promo_code_applied;
        }
      }
      
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        payload,
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Payment initiation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setIsProcessingPayment(false);
    }
  };

  const initiatePayPal = async () => {
    toast.error('PayPal is MOCKED. Add PAYPAL_CLIENT_ID and PAYPAL_SECRET to backend to enable.');
  };

  const handleProofread = async (skipPricing = false) => {
    if (!originalText.trim()) {
      toast.error('Please enter or upload text to proofread');
      return;
    }

    // A style must be a deliberate choice — block proofread until one is picked
    if (!stylePreset) {
      setStyleError(true);
      toast.error('Please choose a style before proofreading', { duration: 4000 });
      const sel = document.querySelector('[data-testid="style-preset-select"]');
      if (sel) {
        sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sel.focus?.();
      }
      // Note: styleError stays on until the user actually picks a style
      // (cleared inside onValueChange) so the rose ring keeps signalling
      // why proofreading didn't start.
      return;
    }

    if (!priceEstimate && !skipPricing) {
      await calculatePrice(null, { autoProofreadIfFree: true });
      return;
    }

    setIsProofreading(true);
    setCorrectedText('');
    setDifferences([]);
    setAnalysisText('');
    setQualityRating(null);
    setProofreadProgress(null);

    // Generate a client_id for this run so the backend can report per-chunk progress
    const clientId = `pf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    let pollTimer = null;

    // async poller invoked from setInterval, not during render
    const pollProgress = async () => {
      try {
        const { data } = await axios.get(`${API}/proofread/progress/${clientId}`, { withCredentials: true });
        if (data && data.total > 0) {
          setProofreadProgress({ current: data.current, total: data.total });
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Progress poll failed (will retry):', err?.message);
        }
      }
    };
    // Only poll for manuscripts/screenplays (songs run as a single quick call)
    if (documentType !== 'song') {
      pollTimer = setInterval(pollProgress, 1500);
    }

    try {
      const response = await axios.post(
        `${API}/proofread`,
        {
          text: originalText,
          document_type: documentType,
          model_provider: modelProvider,
          rush_level: rushLevel,
          style_preset: stylePreset,
          progress_client_id: clientId,
        },
        { withCredentials: true, timeout: 600000 }
      );

      setCorrectedText(response.data.corrected_text);
      setDifferences(response.data.differences);
      setAnalysisText(response.data.analysis_text || '');
      setQualityRating(response.data.quality_rating || null);
      setCurrentSessionId(response.data.session_id);
      setShowPricing(false);
      setPriceEstimate(null);
      toast.success('Proofreading completed!');
      fetchHistory();
      await refreshUser();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Proofreading error:', error);

      // Payment required → distinct path
      if (error.response?.status === HTTP_PAYMENT_REQUIRED) {
        toast.error('Payment required. Please complete payment first.');
        await calculatePrice();
      } else if (
        // Recover from ingress-timeout (502/504) or network drop by polling
        // the progress endpoint until the backend completes the job.
        documentType !== 'song' && (
          !error.response ||
          error.response.status === 502 ||
          error.response.status === 504 ||
          error.code === 'ECONNABORTED'
        )
      ) {
        toast.message('Connection paused — recovering result from server...');
        const recovered = await waitForProofreadResult(clientId);
        if (recovered) {
          setCorrectedText(recovered.corrected_text);
          setDifferences(recovered.differences);
          setAnalysisText(recovered.analysis_text || '');
          setQualityRating(recovered.quality_rating || null);
          setCurrentSessionId(recovered.session_id);
          setShowPricing(false);
          setPriceEstimate(null);
          toast.success('Proofreading completed!');
          fetchHistory();
          await refreshUser();
        } else {
          toast.error('Proofreading is taking longer than expected. Try a smaller chunk or retry shortly.');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Proofreading failed. Please try again.');
      }
    } finally {
      if (pollTimer) clearInterval(pollTimer);
      setIsProofreading(false);
      setProofreadProgress(null);
    }
  };

  /**
   * Polls /proofread/progress/{clientId} until the backend reports status==='done'
   * and includes the result payload, OR status==='error', OR the deadline is hit.
   * Returns the recovered ProofreadResponse, or null on failure.
   */
  const waitForProofreadResult = async (clientId, { maxMs = 240000, intervalMs = 3000 } = {}) => {
    const deadline = Date.now() + maxMs;
    while (Date.now() < deadline) {
      try {
        const { data } = await axios.get(`${API}/proofread/progress/${clientId}`, { withCredentials: true });
        if (data?.total > 0) {
          setProofreadProgress({ current: data.current, total: data.total });
        }
        if (data?.status === 'done' && data.result) {
          return data.result;
        }
        if (data?.status === 'error') {
          return null;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.warn('Recovery poll failed:', err?.message);
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return null;
  };

  const handleDownload = async (format = 'txt') => {
    if (!currentSessionId) {
      toast.error('No session to download');
      return;
    }

    const fmt = format.toLowerCase();
    const isSongPdf = fmt === 'pdf';
    const base = isSongPdf ? 'songwriter_analysis' : `corrected_${documentType}`;
    const filename = `${base}_${currentSessionId.slice(0, 8)}.${fmt}`;

    const attempt = async () => {
      const response = await axios.get(
        `${API}/download/${currentSessionId}?format=${fmt}`,
        { responseType: 'blob', withCredentials: true, timeout: 60000 }
      );
      if (!response.data || response.data.size === 0) {
        throw new Error('Empty response from server');
      }
      return response.data;
    };

    // Retry up to 3× with backoff to absorb the rare network/race blip that
    // would otherwise show as a transient "Download failed" toast.
    let blob = null;
    let lastError = null;
    for (let i = 0; i < 3; i += 1) {
      try {
        blob = await attempt();
        break;
      } catch (err) {
        lastError = err;
        const status = err?.response?.status;
        // Don't retry on hard client errors — they won't fix themselves
        if (status === 401 || status === 403 || status === 404) break;
        if (i < 2) {
          await new Promise((r) => setTimeout(r, 600 * (i + 1)));
        }
      }
    }

    if (!blob) {
      if (process.env.NODE_ENV !== 'production') console.error('Download error after retries:', lastError);
      const status = lastError?.response?.status;
      const detail = lastError?.response?.data?.detail;
      let msg = 'Download failed — please retry in a moment.';
      if (status === 404) msg = 'Session not found. Try refreshing and downloading again.';
      else if (status === 401 || status === 403) msg = 'Please sign in again to download.';
      else if (status === 500) msg = 'The server had trouble generating that file. Try a different format or retry.';
      else if (detail) msg = String(detail).slice(0, 140);
      toast.error(msg, { duration: 6000 });
      return;
    }

    // Trigger the browser download + clean up the object URL afterwards
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    toast.success(`Download started (${fmt.toUpperCase()})`);
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/session/${sessionId}`, { withCredentials: true });
      setOriginalText(response.data.original_text);
      setCorrectedText(response.data.corrected_text);
      setDifferences(response.data.differences);
      setAnalysisText(response.data.analysis_text || '');
      setQualityRating(response.data.quality_rating || null);
      setDocumentType(response.data.document_type);
      setModelProvider(response.data.model_provider);
      setCurrentSessionId(sessionId);
      setShowHistory(false);
      setCompareData(null);
      toast.success('Session loaded');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Session load error:', error);
      toast.error('Failed to load session');
    }
  };

  const handleCompareSelect = (sessionId) => {
    if (selectedSessions.includes(sessionId)) {
      setSelectedSessions(selectedSessions.filter((id) => id !== sessionId));
    } else if (selectedSessions.length < 2) {
      setSelectedSessions([...selectedSessions, sessionId]);
    } else {
      toast.error('You can only compare 2 sessions at a time');
    }
  };

  const compareVersions = async () => {
    if (selectedSessions.length !== 2) {
      toast.error('Please select exactly 2 sessions to compare');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/compare-sessions`,
        {
          session_id_1: selectedSessions[0],
          session_id_2: selectedSessions[1],
        },
        { withCredentials: true }
      );
      setCompareData(response.data);
      setShowCompare(false);
      setOriginalText('');
      setCorrectedText('');
      toast.success('Comparison loaded');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Compare error:', error);
      toast.error('Failed to compare sessions');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Logout error:', error);
    }
  };

  const newProject = () => {
    setOriginalText('');
    setCorrectedText('');
    setDifferences([]);
    setAnalysisText('');
    setQualityRating(null);
    setCurrentSessionId(null);
    setPriceEstimate(null);
    setPromoCode('');
    setCompareData(null);
    setSelectedSessions([]);
    toast.success('Started a new project');
  };

  const renderTextWithHighlights = (diffs = differences) => {
    if (!diffs || diffs.length === 0) {
      return <div className="whitespace-pre-wrap">{correctedText}</div>;
    }

    return (
      <div className="whitespace-pre-wrap">
        {diffs.map((diff, index) => {
          const stableKey = `${diff.type}-${index}-${diff.text.slice(0, 12)}`;
          if (diff.type === 'equal') {
            return <span key={stableKey}>{diff.text}</span>;
          } else if (diff.type === 'insert') {
            return (
              <span
                key={stableKey}
                className="bg-[#27382B] text-[#8CB397] px-1 rounded-sm"
                data-testid={`highlight-insert-${index}`}
              >
                {diff.text}
              </span>
            );
          } else if (diff.type === 'delete') {
            return (
              <span
                key={stableKey}
                className="bg-[#3D2121] text-[#B87B7B] line-through px-1 rounded-sm"
                data-testid={`highlight-delete-${index}`}
              >
                {diff.text}
              </span>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const fontClass = documentType === 'screenplay' ? 'font-mono' : 'font-serif';

  const renderCorrectedPane = () => {
    if (isProofreading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-2 border-border border-t-gold rounded-full animate-spin" />
            <p className="mt-4 text-sm text-ash font-sans">
              {documentType === 'song' ? 'Analysing lyrics...' : 'Proofreading in progress...'}
            </p>
          </div>
        </div>
      );
    }
    if (documentType === 'song' && (analysisText || correctedText)) {
      return (
        <div className="flex items-center justify-center h-full" data-testid="song-lyrics-preserved-pane">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 mx-auto mb-5 rounded-full border border-border bg-card flex items-center justify-center">
              <MusicNote size={22} weight="duotone" className="text-gold" />
            </div>
            <p className="text-base font-serif font-light text-ivory leading-relaxed">
              Lyrics preserved — see analysis panel below.
            </p>
            <p className="text-xs text-ash mt-3 font-sans tracking-wide">
              Songs are not grammar-corrected. Slang, abbreviations and intentional phrasing are kept exactly as written.
            </p>
          </div>
        </div>
      );
    }
    if (correctedText) {
      return renderTextWithHighlights();
    }
    return <p className="text-ash font-sans">Corrected text will appear here...</p>;
  };

  return (
    <div className="w-full h-screen max-h-screen overflow-hidden flex flex-col bg-background" data-testid="dashboard">
      <header className="h-16 border-b border-border flex items-center justify-between px-6 shrink-0" data-testid="header">
        <div className="flex items-center gap-4">
          <div className="h-9 w-12 rounded-md border border-border bg-card flex items-center justify-center">
            <span className="font-serif text-gold text-lg leading-none tracking-tight">MP</span>
          </div>
          <h1 className="text-2xl font-serif font-medium tracking-tight text-ivory">Manuscript Polish</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden md:block">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Welcome, {user?.name}</p>
            {user?.is_admin ? (
              <p className="text-sm text-bone font-sans" data-testid="header-host-label">
                <span className="font-medium text-gold">Host</span> • Unlimited
              </p>
            ) : (
              <p className="text-sm text-bone font-sans">
                {user?.subscription_status === 'active' ? (
                  <>
                    <span className="font-medium text-gold capitalize">{user?.subscription_tier}</span>
                    {' • '}
                    <span className="font-medium">{Math.max(0, (user?.monthly_word_allowance || 0) - (user?.monthly_words_used || 0)).toLocaleString()}</span> mo. words
                    {(user?.free_words_remaining || 0) > 0 && (
                      <> {' • '} <span className="text-emerald-400 font-medium">+{user.free_words_remaining}</span> free</>
                    )}
                  </>
                ) : (
                  <>Free words: <span className="font-medium text-gold">{user?.free_words_remaining || 0}</span></>
                )}
              </p>
            )}
          </div>
          <Button
            onClick={() => navigate('/welcome')}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 md:px-6 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:bg-stone hidden md:inline-flex"
            data-testid="welcome-link-btn"
            title="Visit the public welcome page"
          >
            Welcome
          </Button>
          <Button
            onClick={() => navigate('/creative-partner')}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 md:px-6 py-3 transition-colors duration-200 bg-transparent text-gold border border-gold/40 hover:bg-gold/10 hidden md:inline-flex"
            data-testid="creative-partner-link-btn"
            title="Open Creative Partner"
          >
            <Sparkle size={14} weight="duotone" className="md:mr-2" />
            <span className="hidden md:inline">Creative Partner</span>
          </Button>
          <Button
            onClick={() => navigate('/cinematic-studio')}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 md:px-6 py-3 transition-colors duration-200 bg-transparent text-gold border border-gold/40 hover:bg-gold/10 hidden md:inline-flex"
            data-testid="cinematic-studio-link-btn"
            title="Open Cinematic Studio"
          >
            <FilmSlate size={14} weight="duotone" className="md:mr-2" />
            <span className="hidden md:inline">Cinematic Studio</span>
          </Button>
          <Button
            onClick={() => setShowHistory(!showHistory)}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 md:px-6 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:bg-stone"
            data-testid="history-toggle-btn"
          >
            <Clock size={16} weight="duotone" className="md:mr-2" />
            <span className="hidden md:inline">History</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 md:px-6 py-3 transition-colors duration-200 bg-gold text-charcoal hover:bg-gold-dark border border-transparent"
                data-testid="profile-menu-trigger"
              >
                <User size={16} weight="duotone" className="md:mr-2" />
                <span className="hidden md:inline">Profile</span>
                <CaretDown size={12} weight="bold" className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg border-border min-w-[220px]" data-testid="profile-menu">
              <DropdownMenuLabel className="font-sans">
                <div className="font-medium">{user?.name}</div>
                <div className="text-xs font-normal text-ash">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!user?.is_admin && (
                <>
                  <DropdownMenuItem
                    onClick={() => setShowSubscriptions(true)}
                    className="cursor-pointer text-sm font-sans focus:bg-stone/60"
                    data-testid="profile-subscribe"
                  >
                    <Crown size={16} weight="duotone" className="mr-2" />
                    {user?.subscription_status === 'active' ? 'Manage Subscription' : 'Subscribe'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowExtensions(true)}
                    className="cursor-pointer text-sm font-sans"
                    data-testid="profile-topup"
                  >
                    <Plus size={16} weight="duotone" className="mr-2" />
                    Pricing & Top Up
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={() => navigate('/how-to')}
                className="cursor-pointer text-sm font-sans"
                data-testid="profile-how-to"
              >
                <Question size={16} weight="duotone" className="mr-2" />
                How to use
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/about')}
                className="cursor-pointer text-sm font-sans"
                data-testid="profile-about"
              >
                <Info size={16} weight="duotone" className="mr-2" />
                About
              </DropdownMenuItem>
              {user?.is_admin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/admin')}
                    className="cursor-pointer text-sm font-sans"
                    data-testid="profile-admin"
                  >
                    <ShieldCheck size={16} weight="duotone" className="mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              {!user?.is_admin && (
                <DropdownMenuItem
                  onClick={openBillingPortal}
                  className="cursor-pointer text-sm font-sans"
                  data-testid="profile-billing"
                >
                  <CreditCard size={16} weight="duotone" className="mr-2" />
                  Manage subscription
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-sm font-sans focus:bg-rose-950/40 focus:text-rose-400"
                data-testid="profile-logout"
              >
                <SignOut size={16} weight="duotone" className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 overflow-hidden">
        {showHistory && (
          <div className="hidden md:flex md:col-span-3 lg:col-span-2 border-r border-border bg-stone flex-col overflow-y-auto" data-testid="history-sidebar">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Recent Sessions</label>
                {history.length >= 2 && (
                  <Button
                    onClick={() => setShowCompare(true)}
                    className="rounded-lg text-xs px-2 py-1 bg-gold text-charcoal hover:bg-gold-dark"
                    data-testid="compare-btn"
                  >
                    <GitDiff size={14} weight="duotone" />
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {history.length === 0 ? (
                  <p className="text-sm text-ash">No history yet</p>
                ) : (
                  history.map((item) => (
                    <div
                      key={item.session_id}
                      onClick={() => loadSession(item.session_id)}
                      className="p-3 border border-border bg-background cursor-pointer hover:bg-stone transition-colors duration-150"
                      data-testid={`history-item-${item.session_id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileText size={14} weight="duotone" />
                        <span className="text-xs font-medium uppercase text-ivory">{item.document_type}</span>
                      </div>
                      <p className="text-xs text-bone line-clamp-2">{item.original_text_preview}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-ash">{new Date(item.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-ash">{item.word_count} words</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        <div className={`col-span-1 ${showHistory ? 'md:col-span-9 lg:col-span-10' : 'md:col-span-12'} flex flex-col h-full ${(analysisText || qualityRating) ? 'overflow-y-auto' : 'overflow-hidden'}`}>
          <div className="h-14 border-b border-border flex items-center px-4 gap-4 shrink-0 bg-background" data-testid="toolbar">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Type</label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger className="w-[140px] rounded-lg border border-border bg-background focus:border-gold focus:ring-1 focus:ring-gold" data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="manuscript" data-testid="type-manuscript">Manuscript</SelectItem>
                  <SelectItem value="screenplay" data-testid="type-screenplay">Screenplay</SelectItem>
                  <SelectItem value="song" data-testid="type-song">Song (£1 / song)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Model</label>
              <Select value={modelProvider} onValueChange={setModelProvider}>
                <SelectTrigger className="w-[140px] rounded-lg border border-border bg-background focus:border-gold focus:ring-1 focus:ring-gold" data-testid="model-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="openai" data-testid="model-openai">GPT-5.2</SelectItem>
                  <SelectItem value="gemini" data-testid="model-gemini">Gemini 3 Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Priority</label>
              <Select value={rushLevel} onValueChange={(v) => { setRushLevel(v); setPriceEstimate(null); }}>
                <SelectTrigger className="w-[160px] rounded-lg border border-border bg-background focus:border-gold focus:ring-1 focus:ring-gold" data-testid="rush-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="standard" data-testid="rush-standard">Standard</SelectItem>
                  <SelectItem value="rush" data-testid="rush-rush">Rush (+25%)</SelectItem>
                  <SelectItem value="express" data-testid="rush-express">Express (+50%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 relative">
              <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">
                Style{stylePreset === '' && <span className="text-rose-400 ml-1" data-testid="style-required-asterisk">*</span>}
              </label>
              <Select
                value={stylePreset}
                onValueChange={(v) => { setStylePreset(v); setStyleError(false); }}
              >
                <SelectTrigger
                  className={`w-[200px] rounded-lg border bg-background transition-all duration-300 focus:ring-1 ${
                    styleError
                      ? 'border-rose-400 ring-2 ring-rose-400/40 animate-pulse'
                      : 'border-border focus:border-gold focus:ring-gold'
                  }`}
                  data-testid="style-preset-select"
                >
                  <SelectValue placeholder="Choose a style..." />
                </SelectTrigger>
                <SelectContent className="rounded-lg max-h-72">
                  {(stylePresets[documentType] || []).map((preset) => (
                    <SelectItem key={preset.id} value={preset.id} data-testid={`style-${preset.id}`}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {styleError && (
                <span className="absolute -bottom-5 left-[64px] text-[10px] font-medium text-rose-400 tracking-wide whitespace-nowrap" data-testid="style-required-error">
                  Required to proofread
                </span>
              )}
            </div>

            <Button
              onClick={newProject}
              className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:bg-stone"
              data-testid="new-project-btn"
              title="Start a new project (clears both panes)"
            >
              <FilePlus size={16} weight="duotone" className="mr-2" />
              New
            </Button>

            {!user?.is_admin && (
              <Button
                onClick={() => setShowExtensions(true)}
                className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:bg-stone"
                data-testid="topup-btn"
              >
                <Plus size={16} weight="duotone" className="mr-2" />
                Top Up
              </Button>
            )}

            <div className="flex-1" />

            <label htmlFor="file-upload" className="cursor-pointer">
              <input
                id="file-upload"
                type="file"
                accept=".txt,.docx,.pdf,.fdx,.fountain"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                type="button"
                disabled={isUploading}
                className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-6 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:bg-stone"
                data-testid="upload-btn"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('file-upload').click();
                }}
              >
                <FileArrowUp size={16} weight="duotone" className="mr-2" />
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </label>

            <Button
              onClick={() => handleProofread()}
              disabled={isProofreading || !originalText.trim()}
              className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-6 py-3 transition-colors duration-200 bg-gold text-charcoal hover:bg-gold-dark border border-transparent"
              data-testid="proofread-btn"
            >
              <ArrowsClockwise size={16} weight="duotone" className="mr-2" />
              {isProofreading ? 'Processing...' : 'Proofread'}
            </Button>

            {correctedText && (
              <>
                <Button
                  onClick={() => handleDownload('txt')}
                  className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:bg-stone"
                  data-testid="download-txt-btn"
                >
                  <Download size={16} weight="duotone" className="mr-2" />
                  TXT
                </Button>
                <Button
                  onClick={() => handleDownload('docx')}
                  className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-4 py-3 transition-colors duration-200 bg-transparent text-ivory border border-border hover:border-gold hover:bg-stone"
                  data-testid="download-docx-btn"
                >
                  <FileDoc size={16} weight="duotone" className="mr-2" />
                  DOCX
                </Button>
              </>
            )}
          </div>

          {isProofreading && proofreadProgress && proofreadProgress.total > 1 && (
            <div className="border-b border-border bg-card/40 px-6 py-3" data-testid="proofread-progress">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-[0.28em] text-gold">
                  Polishing {documentType === 'screenplay' ? 'scene' : 'chapter'} {proofreadProgress.current} of {proofreadProgress.total}
                </p>
                <p className="text-xs text-ash font-sans tracking-wide">
                  {Math.round((proofreadProgress.current / proofreadProgress.total) * 100)}%
                </p>
              </div>
              <div className="h-1.5 w-full bg-stone rounded-full overflow-hidden">
                <div
                  className="h-full bg-gold transition-all duration-500 ease-out"
                  style={{ width: `${(proofreadProgress.current / proofreadProgress.total) * 100}%` }}
                  data-testid="progress-bar-fill"
                />
              </div>
            </div>
          )}

          {compareData ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="h-10 border-b border-border flex items-center justify-between px-4 bg-stone">
                <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Version Comparison</label>
                <Button
                  onClick={() => {
                    setCompareData(null);
                    setSelectedSessions([]);
                  }}
                  className="text-xs px-3 py-1 bg-transparent border border-border hover:bg-stone rounded-lg"
                >
                  Close
                </Button>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border overflow-hidden">
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="h-10 border-b border-border flex items-center px-4 bg-background">
                    <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Version 1</label>
                  </div>
                  <div className={`flex-1 overflow-y-auto p-8 md:p-12 pb-32 text-lg leading-loose ${fontClass} bg-background`}>
                    <div className="whitespace-pre-wrap">{compareData.session_1.corrected_text}</div>
                  </div>
                </div>
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="h-10 border-b border-border flex items-center px-4 bg-background">
                    <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Version 2 (Changes)</label>
                  </div>
                  <div className={`flex-1 overflow-y-auto p-8 md:p-12 pb-32 text-lg leading-loose ${fontClass} bg-background`}>
                    {renderTextWithHighlights(compareData.differences)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border overflow-hidden ${(analysisText || qualityRating) ? 'h-[60vh] shrink-0' : 'flex-1'}`} data-testid="comparison-grid">
              <div className="flex flex-col h-full overflow-hidden">
                <div className="h-10 border-b border-border flex items-center justify-between px-4 bg-stone">
                  <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash" data-testid="original-label">Original</label>
                  {originalText && (
                    <button
                      onClick={() => setOriginalText('')}
                      className="text-xs text-ash hover:text-ivory font-medium uppercase tracking-[0.28em] flex items-center gap-1"
                      data-testid="reset-original-btn"
                      title="Clear Original pane"
                    >
                      <ArrowCounterClockwise size={12} weight="duotone" />
                      Reset
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-hidden relative">
                  {!originalText && !correctedText ? (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="empty-state-overlay">
                      <div className="text-center max-w-xs">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-border bg-card flex items-center justify-center">
                          <FileText size={28} weight="duotone" className="text-gold" />
                        </div>
                        <p className="text-lg font-serif font-light text-ivory">Start typing or upload a document</p>
                        <p className="text-xs text-ash mt-3 font-sans tracking-wide">TXT · DOCX · PDF · FDX · Fountain · max 50&nbsp;MB</p>
                      </div>
                    </div>
                  ) : null}
                  <textarea
                    value={originalText}
                    onChange={(e) => setOriginalText(e.target.value)}
                    placeholder={documentType === 'song' ? 'Paste your lyrics here…' : 'Paste or type your text here...'}
                    className={`w-full h-full overflow-y-auto p-8 md:p-12 pb-32 focus:outline-none focus:ring-0 resize-none text-lg leading-loose ${fontClass} bg-transparent text-ivory placeholder:text-ash relative z-10`}
                    data-testid="original-text-area"
                  />
                </div>
              </div>

              <div className="flex flex-col h-full overflow-hidden">
                <div className="h-10 border-b border-border flex items-center justify-between px-4 bg-stone">
                  <label className="text-xs font-medium uppercase tracking-[0.2em] text-ash" data-testid="corrected-label">{documentType === 'song' ? 'Lyrics · Preserved' : 'Corrected'}</label>
                  {correctedText && (
                    <button
                      onClick={() => { setCorrectedText(''); setDifferences([]); setAnalysisText(''); setQualityRating(null); setCurrentSessionId(null); }}
                      className="text-xs text-ash hover:text-ivory font-medium uppercase tracking-[0.28em] flex items-center gap-1"
                      data-testid="reset-corrected-btn"
                      title="Clear Corrected pane"
                    >
                      <ArrowCounterClockwise size={12} weight="duotone" />
                      Reset
                    </button>
                  )}
                </div>
                <div className={`flex-1 overflow-y-auto p-8 md:p-12 pb-32 text-lg leading-loose ${fontClass} bg-background`} data-testid="corrected-text-area">
                  {renderCorrectedPane()}
                </div>
              </div>
            </div>
          )}

          {/* Quality rating panel (manuscript / screenplay / play / drama) */}
          {qualityRating && (
            <QualityPanel
              rating={qualityRating}
              onClose={() => setQualityRating(null)}
              onOpenRewrite={() => setRewriteOpen(true)}
              onOpenPoster={documentType !== 'song' && currentSessionId ? () => setPosterOpen(true) : null}
              onBringToLife={currentSessionId ? () => navigate(`/cinematic-studio?source=${currentSessionId}`) : null}
              isAdmin={user?.is_admin}
            />
          )}

          {/* Song analysis panel (only when proofreading a Song with analysis) */}
          {analysisText && (
            <div className="border-t border-border bg-card/40 shrink-0" data-testid="song-analysis-panel">
              <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-10 animate-fade-up">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg border border-border bg-card flex items-center justify-center">
                      <MusicNote size={18} weight="duotone" className="text-gold" />
                    </div>
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-gold">Songwriter Analysis</p>
                      <p className="text-xs text-ash font-sans">Theme · Structure · Tone · Rhyme · Impact</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                  <button
                    onClick={() => { navigator.clipboard.writeText(analysisText); toast.success('Analysis copied'); }}
                    className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold transition-colors"
                    data-testid="copy-analysis-btn"
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => handleDownload('txt')}
                    className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold transition-colors flex items-center gap-1.5"
                    data-testid="download-analysis-btn"
                    title="Download lyrics + analysis as .txt"
                  >
                    <Download size={12} weight="duotone" />
                    TXT
                  </button>
                  <button
                    onClick={() => handleDownload('pdf')}
                    className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold transition-colors flex items-center gap-1.5"
                    data-testid="download-analysis-pdf-btn"
                    title="Download lyrics + analysis as .pdf"
                  >
                    <Download size={12} weight="duotone" />
                    PDF
                  </button>
                  <button
                    onClick={() => { setAnalysisText(''); setCorrectedText(''); setDifferences([]); setCurrentSessionId(null); toast.success('Analysis closed'); }}
                    className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-rose-400 transition-colors flex items-center gap-1.5"
                    data-testid="close-analysis-btn"
                    title="Close analysis panel"
                  >
                    Close ×
                  </button>
                </div>
                </div>
                <div
                  className="prose prose-invert max-w-none text-bone font-sans leading-[1.85] whitespace-pre-wrap text-sm md:text-base"
                  data-testid="song-analysis-content"
                >
                  {analysisText}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <PricingModal
        open={showPricing}
        onOpenChange={(open) => { setShowPricing(open); if (!open) setPromoCode(''); }}
        priceEstimate={priceEstimate}
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        applyPromoCode={applyPromoCode}
        removePromoCode={removePromoCode}
        isApplyingPromo={isApplyingPromo}
        isProcessingPayment={isProcessingPayment}
        initiatePayment={initiatePayment}
        initiatePayPal={initiatePayPal}
        onTopUpInstead={() => { setShowPricing(false); setShowExtensions(true); }}
        onProceedFree={() => { setShowPricing(false); handleProofread(true); }}
      />

      <CompareModal
        open={showCompare}
        onOpenChange={setShowCompare}
        history={history}
        selectedSessions={selectedSessions}
        onSelect={handleCompareSelect}
        onCompare={compareVersions}
      />

      <SubscriptionsModal
        open={showSubscriptions}
        onOpenChange={setShowSubscriptions}
        user={user}
        plans={subscriptionPlans}
        onSubscribe={subscribeToPlan}
        isProcessingPayment={isProcessingPayment}
      />

      <ExtensionsModal
        open={showExtensions}
        onOpenChange={setShowExtensions}
        user={user}
        extensions={extensions}
        onPurchaseExtension={(extId) => initiatePayment('extension', extId)}
      />

      <RewriteModal
        open={rewriteOpen}
        onClose={() => setRewriteOpen(false)}
        sessionId={currentSessionId}
        isAdmin={user?.is_admin}
      />

      <PosterModal
        open={posterOpen}
        onClose={() => setPosterOpen(false)}
        sessionId={currentSessionId}
        documentType={documentType}
        projectName={qualityRating?.title || documentType}
        isAdmin={user?.is_admin}
      />
    </div>
  );
};
